import { Link } from "react-router-dom";
import { ArrowRight, LucideIcon } from "lucide-react";

interface FeatureStoryProps {
  to: string;
  index: number;
  kicker: string;
  title: string;
  description: string;
  bullets?: string[];
  image: string;
  icon: LucideIcon;
  reverse?: boolean;
  tone?: "cream" | "sage" | "dusk" | "blush";
}

const toneStyles: Record<NonNullable<FeatureStoryProps["tone"]>, string> = {
  cream: "bg-[hsl(var(--background))]",
  sage: "bg-[hsl(35_25%_96%)]",
  dusk: "bg-[hsl(220_20%_96%)]",
  blush: "bg-[hsl(15_35%_96%)]",
};

export function FeatureStory({
  to,
  index,
  kicker,
  title,
  description,
  bullets,
  image,
  icon: Icon,
  reverse,
  tone = "cream",
}: FeatureStoryProps) {
  return (
    <section className={`${toneStyles[tone]} relative overflow-hidden`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div
          className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
            reverse ? "lg:[&>div:first-child]:order-2" : ""
          }`}
        >
          {/* Image */}
          <div className="lg:col-span-7 relative animate-fade-in">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)]">
              <img
                src={image}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>
            {/* Floating number */}
            <div
              className={`hidden lg:flex absolute -top-6 ${
                reverse ? "-right-6" : "-left-6"
              } items-center justify-center w-24 h-24 rounded-full bg-[hsl(var(--background))] shadow-lg border border-border`}
            >
              <span className="font-serif text-4xl text-muted-foreground/60">
                {String(index).padStart(2, "0")}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-5 animate-fade-in" style={{ animationDelay: "0.15s" }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
              <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-medium">
                {kicker}
              </span>
            </div>

            <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground mb-6">
              {title}
            </h3>

            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              {description}
            </p>

            {bullets && bullets.length > 0 && (
              <ul className="space-y-2 mb-8">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-foreground/80">
                    <span className="mt-2 w-1 h-1 rounded-full bg-primary shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}

            <Link
              to={to}
              className="group inline-flex items-center gap-3 rounded-full bg-foreground px-6 py-3 font-serif text-base text-background shadow-sm transition-all hover:bg-primary hover:shadow-md hover:-translate-y-0.5"
            >
              <span>Explore {title}</span>
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background/15 transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
